"use client";
import Image from "next/image";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/authContext";
import { LogOut, Ellipsis } from "lucide-react";
import defaultProfile from "@/images/default-profile.jpg";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface LogoutButtonProps {
  toggleMenu?: () => void;
}

export default function LogoutButton({ toggleMenu }: LogoutButtonProps) {
  const router = useRouter();
  const { logout, user } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogOut = async () => {
    try {
      await logout();
      setShowLogoutDialog(false);
      router.push("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <>
      {/* Desktop View */}
      <div className="hidden lg:flex items-center space-x-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              className="w-full flex items-center p-2 rounded-full hover:bg-secondary cursor-pointer"
              data-cy="desktop-logout-trigger"
            >
              <Image
                src={
                  user?.profile_picture?.url
                    ? user?.profile_picture?.url
                    : defaultProfile
                }
                alt="Profile"
                width={40}
                height={40}
                className="rounded-full mr-2"
              />
              <div className="flex flex-col flex-1 cursor-pointer">
                <span className="text-sm font-bold truncate max-w-32 overflow-hidden whitespace-nowrap">
                  {user?.display_name || user?.name || "User"}
                </span>
                <span className="text-xs text-muted-foreground truncate max-w-32 overflow-hidden whitespace-nowrap">
                  @{user?.name || "user"}
                </span>
              </div>
              <Ellipsis className="ml-2" />
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuItem>
              <button
                onClick={() => setShowLogoutDialog(true)}
                className="text-sm text-destructive dark:text-red-400 hover:text-destructive/90 w-full text-left"
                data-cy="desktop-logout-button"
              >
                Logout @{user?.name || "user"}
              </button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Dialog ,dropdown */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log out</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to log out?
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={handleLogOut}
              data-cy="logout-confirm-button"
            >
              Log out @{user?.name || "user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tablet View  */}
      <div className="hidden sm:flex md:flex lg:hidden items-center justify-center">
        <button
          data-cy="sidebar-logout"
          onClick={handleLogOut}
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          aria-label="Logout"
        >
          <LogOut size={24} strokeWidth={2.5} />
        </button>
      </div>

      {/* Mobile View  */}
      <div className="flex sm:hidden w-full">
        <button
          data-cy="sidebar-logout"
          onClick={handleLogOut}
          className="flex items-center gap-3 px-4 py-3 w-full text-l text-white dark:text-white hover:bg-secondary/10 transition-colors"
        >
          <LogOut size={20} strokeWidth={2.5} />
          Logout
        </button>
      </div>
    </>
  );
}

LogoutButton.displayName = "LogoutButton";
